import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2 } from 'lucide-react';
import Honeypot from '../components/Honeypot';
import StepHeading from '../components/StepHeading';
import TextField from '../components/TextField';
import { BD } from '../bookDemoTheme';
import { isValidEmail } from '../bookDemoConstants';

interface BelowThresholdStepProps {
  email: string;
  website: string;
  onChange: (patch: { email?: string; website?: string }) => void;
  /** Stores the lead; resolves false when the request failed. */
  onSubmit: () => Promise<boolean>;
  onBack: () => void;
  onClose: () => void;
}

/**
 * Soft stop for the `<€1M` tier: no booking path, just an honest message and an
 * optional email capture so we can come back when the business has grown.
 */
const BelowThresholdStep: React.FC<BelowThresholdStepProps> = ({
  email,
  website,
  onChange,
  onSubmit,
  onBack,
  onClose,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setFailed(false);
    const ok = await onSubmit();
    setIsSubmitting(false);
    if (ok) setIsSubmitted(true);
    else setFailed(true);
  };

  if (isSubmitted) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center text-center pb-16">
        <CheckCircle2 size={64} className={`mb-6 ${BD.accentText}`} strokeWidth={1.5} />
        <StepHeading title={t('bookDemo.belowThreshold.thanksTitle')} subtitle={t('bookDemo.belowThreshold.thanksBody')} />
        <button
          type="button"
          onClick={onClose}
          className={`h-11 px-[30px] rounded-xl text-[16px] leading-6 font-semibold ${BD.accentBgDeep} ${BD.textOnAccent}`}
        >
          {t('bookDemo.success.returnHome')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1">
      <StepHeading title={t('bookDemo.belowThreshold.title')} subtitle={t('bookDemo.belowThreshold.body')} />

      <div className={`relative max-w-[606px] mx-auto w-full rounded-xl border p-4 sm:p-5 ${BD.cardBg} ${BD.borderStrong}`}>
        <p className={`text-[14px] leading-5 mb-4 ${BD.textSecondary}`}>{t('bookDemo.belowThreshold.captureIntro')}</p>
        <TextField
          id="bookDemoLeadEmail"
          type="email"
          label={t('bookDemo.contact.email.label')}
          value={email}
          onChange={value => onChange({ email: value })}
          placeholder={t('bookDemo.contact.email.placeholder')}
          autoComplete="email"
        />
        <Honeypot value={website} onChange={value => onChange({ website: value })} />

        {failed && <p className={`mt-3 text-[13px] leading-5 ${BD.errorText}`}>{t('bookDemo.belowThreshold.failed')}</p>}
      </div>

      <div className="sm:px-4 w-full mt-auto pt-8 sm:pt-10 pb-8 sm:pb-12">
        <div className="max-w-[697px] mx-auto w-full flex flex-row items-center justify-center gap-3 sm:gap-6">
          <button
            type="button"
            onClick={onBack}
            className={`border w-full h-11 rounded-xl text-[14px] leading-5 font-semibold transition-colors ${BD.backButton}`}
          >
            {t('bookDemo.nav.back')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValidEmail(email) || isSubmitting}
            className={`w-full h-11 rounded-xl text-[14px] leading-5 font-semibold transition disabled:cursor-not-allowed flex items-center justify-center gap-2 ${BD.primaryButton}`}
          >
            {isSubmitting && <Loader2 size={16} className="animate-spin" />}
            {t('bookDemo.belowThreshold.submit')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BelowThresholdStep;
